import { zodResolver } from "@hookform/resolvers/zod";
import { Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";
import { getErrorDetails } from "../../app/appUtils";
import AppButton from "../../components/ui/AppButton";
import AppDatePicker from "../../components/ui/AppDatePicker";
import AppInputField from "../../components/ui/AppInputField";
import { Spinner } from "../../components/ui/common";
import { FileUpload } from "../../components/ui/FileUpload";
import { Modal } from "../../components/ui/Modal";
import { MySelect } from "../../components/ui/Select";
import SuccessView from "../../components/ui/SuccessView";
import { useToast } from "../../hooks/useToast";
import { processFile } from "../../lib/utils";
import BrandDropdownMenu from "../brand/components/BrandDropdownMenu";
import PartnerDropdownMenu from "../partner/ui/PartnerDropdownMenu";
import { Vehicle } from "./@types";
import VehicleTypesDropdown from "./ui/VehicleTypesDropdown";
import {
  useAddVehicleMutation,
  useUpdateVehicleMutation,
} from "./vehicleApiSlice";

const documentSchema = z.object({
  title: z.string().nonempty("Document title is required"),
  startDate: z.string().nonempty("Start date is required"),
  expiryDate: z.string().nonempty("Expiry date is required"),
});

export const addVehicleSchema = z.object({
  vehicleName: z.string().nonempty("Vehicle name is required"),
  vehicleModel: z.string().nonempty("Vehicle model is required"),
  vehicleType: z.string().nonempty("Vehicle type is required"),
  incomeTarget: z.number().nonnegative("Income target is required"),
  landingCost: z.number().nonnegative("Landing cost is required"),
  licensePlate: z.string().nonempty("License plate is required"),
  modelYear: z.string().nonempty("Year is required"),
  datePurchased: z.string().nonempty("Date of purchase is required"),
  chassisNumber: z.string().nonempty("Chassis number is required"),
  partnerId: z.string().optional(),
  brandId: z.string().nonempty("Brand is required"),
  maintenanceRule: z.string().nonempty("Maintenance rule is required"),
  image: z.any().refine((file) => file, "Main image is required"),
  documents: z.array(documentSchema).optional(),
});

export type VehicleModelForm = z.infer<typeof addVehicleSchema>;

interface VehicleModalProps {
  isOpen: boolean;
  onClose: () => void;
  vehicle?: Vehicle | null;
  mode?: "add" | "edit";
}

const VehicleModal: React.FC<VehicleModalProps> = ({
  isOpen,
  onClose,
  vehicle = null,
  mode = vehicle ? "edit" : "add",
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const toast = useToast();
  const isEditMode = mode === "edit" && vehicle;

  const [
    addVehicle,
    {
      isLoading: isCreating,
      isSuccess: createSuccess,
      isError: createError,
      error: createErrorData,
      reset: resetCreate,
    },
  ] = useAddVehicleMutation();

  const [
    updateVehicle,
    {
      isLoading: isUpdating,
      isSuccess: updateSuccess,
      isError: updateError,
      error: updateErrorData,
      reset: resetUpdate,
    },
  ] = useUpdateVehicleMutation();

  // Derived states for cleaner code
  const isLoading = isCreating || isUpdating;
  const isSuccess = createSuccess || updateSuccess;
  const isError = createError || updateError;
  const error = createErrorData || updateErrorData;

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    watch,
    setValue,
    reset,
    clearErrors,
  } = useForm<VehicleModelForm>({
    resolver: zodResolver(addVehicleSchema),
    mode: "onChange",
    defaultValues: {
      vehicleName: "",
      vehicleModel: "",
      vehicleType: "",
      licensePlate: "",
      incomeTarget: 0,
      landingCost: 0,
      chassisNumber: "",
      partnerId: "",
      brandId: "",
      maintenanceRule: "",
      modelYear: "",
      datePurchased: "",
      image: null,
      documents: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "documents",
  });

  useEffect(() => {
    if (isEditMode && vehicle) {
      reset({
        vehicleName: vehicle.vehicleName,
        vehicleModel: vehicle.vehicleModel,
        vehicleType: vehicle.vehicleType,
        licensePlate: vehicle.licensePlate,
        chassisNumber: vehicle.chassisNumber,
        incomeTarget: vehicle.incomeTarget,
        landingCost: vehicle.landingCost,
        partnerId: vehicle.partnerId,
        brandId: vehicle.brandId || "",
        maintenanceRule: vehicle.maintenanceRule || "",
        image: vehicle.vehicleImageUrl,
        modelYear: vehicle.modelYear,
        datePurchased: vehicle.datePurchased,
        documents: vehicle.documents || [],
      });
    } else if (!isEditMode) {
      reset({
        vehicleName: "",
        vehicleModel: "",
        vehicleType: "",
        licensePlate: "",
        chassisNumber: "",
        incomeTarget: 0,
        landingCost: 0,
        partnerId: "",
        brandId: "",
        maintenanceRule: "",
        image: null,
        modelYear: "",
        datePurchased: "",
        documents: [],
      });
    }
  }, [isEditMode, vehicle, reset]);

  const handleFileUpload = (
    name: keyof VehicleModelForm,
    file: File | null,
  ) => {
    setValue(name, file, { shouldValidate: true, shouldDirty: true });
    if (file) {
      clearErrors(name);
    }
  };

  const onSubmit = async (data: VehicleModelForm) => {
    if (isSubmitting || isLoading) return;

    if (isEditMode && !isDirty) {
      toast.warning({
        title: "No Changes",
        message: "No changes were made to update.",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const imageData = await processFile(data.image, "image");

      const payload = {
        vehicleName: data.vehicleName,
        vehicleModel: data.vehicleModel,
        vehicleType: data.vehicleType,
        licensePlate: data.licensePlate,
        chassisNumber: data.chassisNumber,
        incomeTarget: data.incomeTarget,
        landingCost: data.landingCost,
        partnerId: data.partnerId,
        brandId: data.brandId,
        maintenanceRule: data.maintenanceRule,
        image: imageData,
        modelYear: data.modelYear,
        datePurchased: data.datePurchased,
        documents: data.documents || [],
      };

      if (isEditMode && vehicle?.id) {
        await updateVehicle({
          id: vehicle.id,
          ...payload,
        }).unwrap();
      } else {
        await addVehicle(payload).unwrap();
      }

      setShowSuccess(true);
    } catch (err) {
      console.error(`${isEditMode ? "Update" : "Create"} error:`, err);
      const errorMessage =
        getErrorDetails(err).message ||
        `Failed to ${isEditMode ? "update" : "add"} vehicle. Please try again.`;

      toast.error({
        title: "Error",
        message: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (isSuccess && !showSuccess) {
      toast.success({
        title: "Success",
        message: `Vehicle ${isEditMode ? "updated" : "added"} successfully`,
      });
      setShowSuccess(true);
    }

    if (isError) {
      const errorMessage =
        getErrorDetails(error).message ||
        `Failed to ${isEditMode ? "update" : "add"} vehicle. Please try again.`;

      toast.error({
        title: "Error",
        message: errorMessage,
      });
    }
  }, [isSuccess, isError, error, toast, isEditMode, showSuccess]);

  const handleCloseModal = () => {
    setShowSuccess(false);
    reset();
    resetCreate();
    resetUpdate();
    onClose();
  };

  const renderSuccessView = () => {
    if (showSuccess) {
      return (
        <SuccessView
          handleCloseModal={handleCloseModal}
          heading=""
          message={`Vehicle has been ${
            isEditMode ? "updated" : "added"
          } successfully.`}
        />
      );
    }
    return null;
  };

  const modalTitle = showSuccess
    ? ""
    : `${isEditMode ? "Edit" : "Add"} vehicle`;
  const submitButtonText = isEditMode ? "Update vehicle" : "+ Add vehicle";

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCloseModal}
      title={modalTitle}
      className="max-w-full sm:max-w-[80vw] lg:max-w-[45vw] w-full bg-white px-2 pb-8 overflow-hidden"
    >
      {isSuccess ? (
        renderSuccessView()
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
            <div className="space-y-4">
              <div className="w-full max-w-[200px] lg:w-[12vw]">
                <Controller
                  control={control}
                  name="image"
                  render={({ field }) => (
                    <FileUpload
                      label="Vehicle photo"
                      value={field.value}
                      onChange={(file) => handleFileUpload("image", file)}
                      error={errors.image?.message?.toString()}
                    />
                  )}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Controller
                  control={control}
                  name="vehicleName"
                  render={({ field }) => (
                    <AppInputField
                      label="Vehicle Name"
                      type="text"
                      error={errors.vehicleName?.message}
                      {...field}
                      className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                      placeholder="Enter vehicle name"
                    />
                  )}
                />

                <Controller
                  control={control}
                  name="vehicleModel"
                  render={({ field }) => (
                    <AppInputField
                      label="Vehicle model"
                      type="text"
                      error={errors.vehicleModel?.message}
                      {...field}
                      className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                      placeholder="Enter vehicle model"
                    />
                  )}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Controller
                  control={control}
                  name="modelYear"
                  render={({ field }) => (
                    <AppInputField
                      label="Model year"
                      type="text"
                      error={errors.modelYear?.message}
                      {...field}
                      className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                      placeholder="Enter vehicle year"
                    />
                  )}
                />
                <Controller
                  name="datePurchased"
                  control={control}
                  render={({ field }) => (
                    <AppDatePicker
                      label="Date purchased"
                      value={field.value ? new Date(field.value) : null}
                      onChange={(date) =>
                        field.onChange(
                          date ? date.toLocaleDateString("en-CA") : "",
                        )
                      }
                      error={errors.datePurchased?.message}
                      className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                    />
                  )}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Controller
                  control={control}
                  name="incomeTarget"
                  render={({ field }) => (
                    <AppInputField
                      label="Enter income target"
                      type="number"
                      error={errors.incomeTarget?.message}
                      {...field}
                      onChange={(e) => field.onChange(e.target.valueAsNumber)}
                      className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                      placeholder="Enter income target"
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="landingCost"
                  render={({ field }) => (
                    <AppInputField
                      label="Enter landing cost"
                      type="number"
                      error={errors.landingCost?.message}
                      {...field}
                      onChange={(e) => field.onChange(e.target.valueAsNumber)}
                      className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                      placeholder="Enter landing cost"
                    />
                  )}
                />
              </div>

              <VehicleTypesDropdown
                value={watch("vehicleType")}
                onChange={(value) =>
                  setValue("vehicleType", value, { shouldValidate: true })
                }
                className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px] mb-6"
                error={errors.vehicleType?.message}
              />

              <PartnerDropdownMenu
                value={watch("partnerId")}
                onChange={(value) =>
                  setValue("partnerId", value ?? undefined, {
                    shouldValidate: true,
                  })
                }
                className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px] mb-6"
                error={errors.partnerId?.message}
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Controller
                  control={control}
                  name="brandId"
                  render={({ field }) => (
                    <BrandDropdownMenu
                      value={watch("brandId")}
                      onChange={(value) =>
                        setValue("brandId", value, {
                          shouldValidate: true,
                        })
                      }
                      className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                      error={errors.brandId?.message}
                      placeholder="Select a brand"
                    />
                  )}
                />

                <div>
                  <label className="font-semibold text-[13px]">
                    Select maintenance rule
                  </label>
                  <Controller
                    control={control}
                    name="maintenanceRule"
                    render={({ field }) => (
                      <MySelect
                        options={["Strict", "Allow"]}
                        onChange={field.onChange}
                        value={field.value}
                        name={field.name}
                        placeholder="Select maintenance rule"
                        getName={(option) => option}
                        getValue={(option) => option}
                        className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px] mt-2"
                      />
                    )}
                  />
                  {errors.maintenanceRule && (
                    <p className="text-primary text-sm mt-1">
                      {errors.maintenanceRule.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="">
                <Controller
                  control={control}
                  name="licensePlate"
                  render={({ field }) => (
                    <AppInputField
                      label="License Plate"
                      type="text"
                      error={errors.licensePlate?.message}
                      {...field}
                      className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                      placeholder="Enter license plate"
                    />
                  )}
                />
              </div>

              <div className="">
                <Controller
                  control={control}
                  name="chassisNumber"
                  render={({ field }) => (
                    <AppInputField
                      label="Registration Number"
                      type="text"
                      error={errors.chassisNumber?.message}
                      {...field}
                      className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                      placeholder="Enter registration number"
                    />
                  )}
                />
              </div>

              {/* Documents Section */}
              <div className="space-y-4 mt-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-md font-medium">Documents</h3>
                  <AppButton
                    type="button"
                    onClick={() =>
                      append({ title: "", startDate: "", expiryDate: "" })
                    }
                    className="px-4 py-1 bg-primary text-white rounded-md hover:bg-primary/75 transition-colors text-sm"
                  >
                    + Add Document
                  </AppButton>
                </div>

                {fields.map((field, index) => (
                  <div
                    key={field.id}
                    className="p-4 border border-gray-200 rounded-md space-y-4"
                  >
                    <div className="flex justify-between items-start">
                      <h4 className="text-sm font-medium">
                        Document {index + 1}
                      </h4>
                      <div
                        onClick={() => remove(index)}
                        className="text-red-500 hover:text-red-700 text-sm bg-white border border-red-500 p-1 rounded-full"
                      >
                        <Trash2 className="h-4 w-4" />
                      </div>
                    </div>

                    <Controller
                      control={control}
                      name={`documents.${index}.title`}
                      render={({ field }) => (
                        <AppInputField
                          label="Document Title"
                          type="text"
                          error={errors.documents?.[index]?.title?.message}
                          {...field}
                          className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                          placeholder="Enter document title"
                        />
                      )}
                    />

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Controller
                        control={control}
                        name={`documents.${index}.startDate`}
                        render={({ field }) => (
                          <AppDatePicker
                            label="Start Date"
                            value={field.value ? new Date(field.value) : null}
                            onChange={(date) =>
                              field.onChange(
                                date ? date.toLocaleDateString("en-CA") : "",
                              )
                            }
                            error={
                              errors.documents?.[index]?.startDate?.message
                            }
                            className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                          />
                        )}
                      />

                      <Controller
                        control={control}
                        name={`documents.${index}.expiryDate`}
                        render={({ field }) => (
                          <AppDatePicker
                            label="Expiry Date"
                            value={field.value ? new Date(field.value) : null}
                            onChange={(date) =>
                              field.onChange(
                                date ? date.toLocaleDateString("en-CA") : "",
                              )
                            }
                            error={
                              errors.documents?.[index]?.expiryDate?.message
                            }
                            className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                          />
                        )}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-center items-center space-x-4 text-[15px] mt-6">
            <AppButton
              type="button"
              onClick={handleCloseModal}
              disabled={isSubmitting || isLoading}
              className="px-6 py-2 bg-[#F6F6F6] rounded-md text-hColor hover:bg-gray-50 transition-colors text-[15px] disabled:opacity-50"
            >
              Cancel
            </AppButton>

            <AppButton
              type="submit"
              disabled={isSubmitting || isLoading}
              className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary/75 transition-colors disabled:opacity-50 text-[15px]"
            >
              {isSubmitting || isLoading ? (
                <>
                  <Spinner /> {isEditMode ? "Updating..." : "Adding..."}
                </>
              ) : (
                submitButtonText
              )}
            </AppButton>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default VehicleModal;
